import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const AdminSetPasswordDialog = ({ user, open, onOpenChange, onSave }) => {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setPassword(''); setConfirmation(''); setReason(''); setVisible(false); setError('');
    }
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirmation) return setError('The password entries do not match.');
    setBusy(true); setError('');
    try {
      await onSave(password, reason.trim());
      onOpenChange(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Could not set the password.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-md" data-testid="admin-set-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Set user password</DialogTitle>
          <DialogDescription data-testid="admin-set-password-user">{user?.email || user?.user_id}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-set-password-form">
          <div className="space-y-1.5">
            <Label htmlFor="admin-new-password">New password</Label>
            <div className="relative">
              <Input id="admin-new-password" type={visible ? 'text' : 'password'} value={password} onChange={(event) => { setPassword(event.target.value); setError(''); }} minLength={8} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="pr-10" data-testid="admin-set-password-new" />
              <button type="button" onClick={() => setVisible((value) => !value)} className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground" aria-label={visible ? 'Hide new password' : 'Show new password'} title={visible ? 'Hide password' : 'Show password'} data-testid="admin-set-password-visibility-toggle">{visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-confirm-password">Confirm password</Label>
            <Input id="admin-confirm-password" type={visible ? 'text' : 'password'} value={confirmation} onChange={(event) => { setConfirmation(event.target.value); setError(''); }} minLength={8} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="admin-set-password-confirm" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password-reason">Reason for change</Label>
            <Input id="admin-password-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={280} placeholder="Support ticket or account-recovery reason" data-testid="admin-set-password-reason" />
          </div>
          {error && <p className="rounded-sm border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" role="alert" data-testid="admin-set-password-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="admin-set-password-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || password.length < 8 || confirmation.length < 8} data-testid="admin-set-password-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Confirm password change</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};